/**
 * Asrary Shopify Theme - Main JavaScript
 * Handles AJAX Cart Drawer, Navigation, Product Form, Accordions, Sticky Mobile ATC
 */

document.addEventListener('DOMContentLoaded', () => {
  initCarousels();
  initHeroSlides();
  initCatalogViews();
  initOrderNotes();
  initContactDraft();
  initStickyHeader();
  initMobileNav();
  initSearchModal();
  initCartDrawer();
  initProductPage();
  initAccordions();
  initStickyMobileATC();
});

/* ==========================================================================
   1. Sticky Header
   ========================================================================== */
function initStickyHeader() {
  const header = document.querySelector('.site-header');
  if (!header) return;

  const handleScroll = () => {
    if (window.scrollY > 30) {
      header.classList.add('is-scrolled');
    } else {
      header.classList.remove('is-scrolled');
    }
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
}

/* ==========================================================================
   2. Mobile Navigation Drawer
   ========================================================================== */
function initMobileNav() {
  const toggleBtn = document.querySelector('.mobile-nav-toggle');
  const drawer = document.querySelector('.mobile-nav-drawer');
  const overlay = document.querySelector('.drawer-overlay');
  const closeBtn = document.querySelector('.mobile-nav-close');

  if (!toggleBtn || !drawer) return;

  const openDrawer = () => {
    drawer.classList.add('is-active');
    overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    drawer.classList.remove('is-active');
    overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
  };

  bindPanelAccessibility(drawer, [toggleBtn], closeBtn);
  toggleBtn.addEventListener('click', openDrawer);
  closeBtn?.addEventListener('click', closeDrawer);
  overlay?.addEventListener('click', closeDrawer);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-active')) {
      closeDrawer();
    }
  });
}

/* ==========================================================================
   3. Search Modal
   ========================================================================== */
function initSearchModal() {
  const searchToggles = document.querySelectorAll('[data-search-toggle]');
  const searchModal = document.querySelector('.search-modal');
  const searchClose = document.querySelector('.search-modal-close');
  const overlay = document.querySelector('.drawer-overlay');

  if (!searchModal) return;

  const openSearch = () => {
    searchModal.classList.add('is-active');
    overlay?.classList.add('is-active');
    const input = searchModal.querySelector('input[type="search"]');
    if (input) setTimeout(() => input.focus(), 100);
  };

  const closeSearch = () => {
    searchModal.classList.remove('is-active');
    if (!document.querySelector('.cart-drawer.is-active') && !document.querySelector('.mobile-nav-drawer.is-active')) {
      overlay?.classList.remove('is-active');
    }
  };

  searchToggles.forEach(toggle => toggle.addEventListener('click', openSearch));
  searchClose?.addEventListener('click', closeSearch);
  overlay?.addEventListener('click', closeSearch);
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeSearch(); });
  bindPanelAccessibility(searchModal, searchToggles, searchClose);
}

/* ==========================================================================
   4. AJAX Cart Drawer Engine
   ========================================================================== */
function initCartDrawer() {
  const cartToggles = document.querySelectorAll('[data-cart-toggle]');
  const cartDrawer = document.querySelector('.cart-drawer');
  const cartClose = document.querySelector('.cart-drawer-close');
  const overlay = document.querySelector('.drawer-overlay');

  const openCart = () => {
    cartDrawer?.classList.add('is-active');
    overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
  };

  const closeCart = () => {
    cartDrawer?.classList.remove('is-active');
    overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
  };

  cartToggles.forEach(btn => btn.addEventListener('click', (e) => {
    e.preventDefault();
    openCart();
  }));

  cartClose?.addEventListener('click', closeCart);
  overlay?.addEventListener('click', closeCart);

  // Global Cart API helper methods
  window.AsraryCart = {
    open: openCart,
    close: closeCart,

    // Add to cart via AJAX
    addItem: async (formData) => {
      try {
        const response = await fetch(shopRoute('cart/add.js'), {
          method: 'POST',
          body: formData
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.description || window.AsrarySettings.error);
        }

        const added = await response.json();
        showAddedPopup(added);
        await window.AsraryCart.refresh();
      } catch (error) {
        showCartError(error.message);
      }
    },

    // Change quantity or remove item
    changeItem: async (lineKey, quantity) => {
      try {
        const response = await fetch(shopRoute('cart/change.js'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: quantity })
        });

        if (!response.ok) { const data = await response.json(); throw new Error(data.description || window.AsrarySettings.error); }
        await window.AsraryCart.refresh();
      } catch (error) {
        showCartError(error.message);
      }
    },

    // Fetch updated cart JSON and re-render drawer elements
    refresh: async () => {
      try {
        const res = await fetch(shopRoute('cart.js'));
        const cart = await res.json();

        // Update header bubbles
        const countBubbles = document.querySelectorAll('.cart-count-bubble');
        countBubbles.forEach(b => {
          b.textContent = cart.item_count > 0 ? cart.item_count : '';
        });

        // Re-render drawer content
        const itemsContainer = document.querySelector('.cart-drawer__items');
        const footerContainer = document.querySelector('.cart-drawer__footer');
        const subtotalElement = document.querySelector('.cart-drawer__subtotal-price');

        if (!itemsContainer) return;

        if (cart.items.length === 0) {
          itemsContainer.innerHTML = `
            <div class="cart-drawer__empty">
              <p>سلتك فارغة حالياً</p>
              <a href="/collections/all" class="btn btn--primary">تصفح منتجاتنا</a>
            </div>
          `;
          if (footerContainer) footerContainer.style.display = 'none';
        } else {
          if (footerContainer) footerContainer.style.display = 'block';
          if (subtotalElement) {
            subtotalElement.textContent = formatMoney(cart.total_price);
          }

          itemsContainer.innerHTML = cart.items.map(item => `
            <div class="cart-item" data-line-key="${item.key}">
              <div class="cart-item__image">
                ${item.image ? `<img src="${item.image}" alt="${escapeHtml(item.title)}">` : ''}
              </div>
              <div class="cart-item__details">
                <div class="cart-item__title">${escapeHtml(item.product_title)}</div>
                ${item.variant_title ? `<div class="cart-item__variant">${escapeHtml(item.variant_title)}</div>` : ''}
                <div class="cart-item__price cart-item__unit-price" dir="ltr"><bdi>${formatMoney(item.final_price)}</bdi><span aria-hidden="true"> × </span><span>${item.quantity}</span></div>
                <div class="quantity-selector cart-item__quantity">
                  <button type="button" class="quantity-btn" data-action="decrease" data-key="${item.key}" data-qty="${item.quantity}">-</button>
                  <input type="text" class="quantity-input" value="${item.quantity}" readonly>
                  <button type="button" class="quantity-btn" data-action="increase" data-key="${item.key}" data-qty="${item.quantity}">+</button>
                </div>
              </div>
              <button type="button" class="cart-item__remove" data-action="remove" data-key="${item.key}" title="حذف">
                ✕
              </button>
            </div>
          `).join('');

          // Re-bind click events on newly rendered drawer items
          bindCartItemButtons();
        }
      } catch (e) {
        showCartError(window.AsrarySettings.error);
      }
    }
  };

  function bindCartItemButtons() {
    document.querySelectorAll('.cart-item [data-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        const key = btn.dataset.key;
        const currentQty = parseInt(btn.dataset.qty, 10);

        if (action === 'remove') {
          window.AsraryCart.changeItem(key, 0);
        } else if (action === 'increase') {
          window.AsraryCart.changeItem(key, currentQty + 1);
        } else if (action === 'decrease') {
          window.AsraryCart.changeItem(key, Math.max(0, currentQty - 1));
        }
      });
    });
  }

  bindCartItemButtons();
  document.addEventListener('keydown', e => { if (e.key === 'Escape') closeCart(); });
  bindPanelAccessibility(cartDrawer, cartToggles, cartClose);

  // Intercept all product form submissions for AJAX add-to-cart
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (form.matches('form[action*="/cart/add"]')) {
      e.preventDefault();
      if (form.dataset.busy) return;
      const formData = new FormData(form);
      form.dataset.busy = 'true';
      const button = form.querySelector('[type="submit"]');
      if (button) button.disabled = true;
      form.setAttribute('aria-busy', 'true');
      document.getElementById('CartFeedback').hidden = true;
      try { await window.AsraryCart.addItem(formData); }
      finally {
        delete form.dataset.busy;
        form.removeAttribute('aria-busy');
        if (button) button.disabled = false;
        form.dispatchEvent(new Event('variant:refresh'));
      }
    }
  });
}

/* ==========================================================================
   5. Product Page Interactions (Gallery, Variants, Quantity)
   ========================================================================== */
function initProductPage() {
  // Gallery thumbnails switch
  const thumbs = document.querySelectorAll('.product__thumb');
  const mainImage = document.querySelector('.product__main-image img');

  thumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      thumbs.forEach(t => t.classList.remove('is-active'));
      thumb.classList.add('is-active');
      const newSrc = thumb.dataset.fullSrc;
      if (mainImage && newSrc) {
        mainImage.removeAttribute('srcset');
        mainImage.src = newSrc;
        mainImage.alt = thumb.querySelector('img')?.alt || '';
      }
    });
  });

  initVariants();

  // Quantity Selector
  document.querySelectorAll('.product-form .quantity-selector').forEach(wrapper => {
    const input = wrapper.querySelector('.quantity-input');
    const plusBtn = wrapper.querySelector('[data-action="increase"]');
    const minusBtn = wrapper.querySelector('[data-action="decrease"]');

    if (!input) return;

    plusBtn?.addEventListener('click', () => {
      let val = parseInt(input.value, 10) || 1;
      input.value = val + 1;
    });

    minusBtn?.addEventListener('click', () => {
      let val = parseInt(input.value, 10) || 1;
      if (val > 1) {
        input.value = val - 1;
      }
    });
  });
}

/* ==========================================================================
   6. Accordion (FAQ & Information)
   ========================================================================== */
function initAccordions() {
  const items = document.querySelectorAll('.accordion__item');

  items.forEach(item => {
    const header = item.querySelector('.accordion__header');
    header?.setAttribute('aria-expanded', String(item.classList.contains('is-open')));
    header?.addEventListener('click', () => {
      const isOpen = item.classList.contains('is-open');
      // Optional: close other items
      // items.forEach(i => i.classList.remove('is-open'));
      if (isOpen) {
        item.classList.remove('is-open');
      } else {
        item.classList.add('is-open');
      }
      header.setAttribute('aria-expanded', String(!isOpen));
    });
  });
}

/* ==========================================================================
   7. Sticky Mobile Add to Cart Bar
   ========================================================================== */
function initStickyMobileATC() {
  const stickyBar = document.querySelector('.sticky-atc-bar');
  const mainAtcBtn = document.querySelector('.product-form__submit');

  if (!stickyBar || !mainAtcBtn) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      // When the main button is not in viewport, show sticky bar
      if (!entry.isIntersecting) {
        stickyBar.classList.add('is-visible');
      } else {
        stickyBar.classList.remove('is-visible');
      }
    });
  }, {
    threshold: 0.1
  });

  observer.observe(mainAtcBtn);

  // When clicking the sticky button, submit the main form
  const stickySubmit = stickyBar.querySelector('.sticky-atc__btn');
  stickySubmit?.addEventListener('click', (e) => {
    e.preventDefault();
    mainAtcBtn.click();
  });
}

/* ==========================================================================
   Helpers
   ========================================================================== */
function formatMoney(cents) {
  return new Intl.NumberFormat('ar-EG', { style: 'currency', currency: window.Shopify?.currency?.active || window.AsrarySettings?.currency || 'EGP' }).format(cents / 100);
}

function escapeHtml(text) {
  if (!text) return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;'
  };
  return String(text).replace(/[&<>"']/g, m => map[m]);
}

function shopRoute(path) {
  return (window.Shopify?.routes?.root || '/') + path;
}

function showCartError(message) {
  const feedback = document.getElementById('CartFeedback');
  feedback.textContent = message || window.AsrarySettings.error;
  feedback.hidden = false;
}

function bindPanelAccessibility(panel, triggers, closeButton) {
  if (!panel) return;
  let previous;
  panel.inert = !panel.classList.contains('is-active');
  new MutationObserver(() => {
    const open = panel.classList.contains('is-active');
    panel.inert = !open;
    if (!open && previous) previous.focus();
  }).observe(panel, { attributes: true, attributeFilter: ['class'] });
  triggers.forEach(trigger => trigger.addEventListener('click', () => {
    previous = trigger;
    panel.inert = false;
    setTimeout(() => (panel.querySelector('input') || closeButton)?.focus(), 50);
  }));
  panel.addEventListener('keydown', e => {
    if (e.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll('a[href],button:not([disabled]),input,select,textarea')].filter(el => el.getClientRects().length);
    const first = focusable[0], last = focusable.at(-1);
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
    if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
  });
}

function initVariants() {
  const form = document.querySelector('.product-form');
  const data = document.querySelector('[data-product-variants]');
  if (!form || !data) return;
  const variants = JSON.parse(data.textContent);
  const update = () => {
    const values = [...form.querySelectorAll('.variant-picker__radio-input:checked')].map(input => input.value);
    const variant = values.length ? variants.find(v => v.options.every((option, i) => option === values[i])) : variants.find(v => String(v.id) === form.elements.id.value);
    form.elements.id.value = variant?.id || '';
    form.querySelectorAll('[name="add"]').forEach(button => {
      button.disabled = !variant?.available || !!form.dataset.busy;
      button.textContent = variant ? (variant.available ? window.AsrarySettings.add : window.AsrarySettings.sold) : window.AsrarySettings.unavailable;
    });
    const sticky = document.querySelector('.sticky-atc__btn');
    if (sticky) { sticky.disabled = !variant?.available || !!form.dataset.busy; sticky.textContent = form.querySelector('[name="add"]').textContent; }
    if (!variant) return;
    const price = document.querySelector('.product__price-box');
    price.textContent = formatMoney(variant.price);
    if (variant.compare_at_price > variant.price) {
      const old = document.createElement('s'); old.textContent = formatMoney(variant.compare_at_price); price.append(' ', old);
    }
    const stickyPrice = document.querySelector('.sticky-atc__price');
    if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
    if (variant.featured_image?.src) {
      const image = document.querySelector('.product__main-image img');
      if (image) { image.removeAttribute('srcset'); image.src = variant.featured_image.src; }
    }
    const url = new URL(location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
  };
  form.addEventListener('change', e => { if (e.target.matches('.variant-picker__radio-input')) update(); });
  form.addEventListener('variant:refresh', update);
  update();
}

let recommendationRequest = 0;
async function showAddedPopup(added) {
  const popup = document.getElementById('AddedPopup');
  const request = ++recommendationRequest;
  popup.querySelector('[data-added-title]').textContent = added.product_title || added.title;
  const section = popup.querySelector('[data-recommendations]');
  const grid = popup.querySelector('.recommendation-grid');
  section.hidden = true; grid.replaceChildren();
  if (!popup.open) popup.showModal();
  popup.querySelectorAll('[data-popup-close]').forEach(button => button.onclick = () => popup.close());
  popup.onclick = e => { if (e.target === popup && (e.clientX < popup.getBoundingClientRect().left || e.clientX > popup.getBoundingClientRect().right || e.clientY < popup.getBoundingClientRect().top || e.clientY > popup.getBoundingClientRect().bottom)) popup.close(); };
  try {
    const response = await fetch(shopRoute(`recommendations/products.json?product_id=${added.product_id}&limit=8&intent=related`));
    if (!response.ok) return;
    const result = await response.json();
    const cartResponse = await fetch(shopRoute('cart.js'));
    if (!cartResponse.ok) return;
    const cart = await cartResponse.json();
    if (request !== recommendationRequest) return;
    const inCart = new Set(cart.items.map(item => item.product_id));
    let candidates = (result.products || []).filter(product => product.available && !inCart.has(product.id));
    if (!candidates.length) {
      const fallbackResponse = await fetch(shopRoute('?section_id=cart-suggestions'));
      if (fallbackResponse.ok) {
        const parsed = new DOMParser().parseFromString(await fallbackResponse.text(), 'text/html');
        candidates = [...parsed.querySelectorAll('[data-suggestion]')].map(el => JSON.parse(el.textContent)).filter(product => product.available && !inCart.has(product.id));
      }
    }
    if (request !== recommendationRequest) return;
    for (const product of candidates.slice(0, 4)) {
      const link = document.createElement('a'); link.className = 'recommendation-card'; link.href = product.url;
      if (product.featured_image) {
        const image = document.createElement('img'); image.src = product.featured_image; image.alt = product.title; image.loading = 'lazy'; link.append(image);
      }
      const title = document.createElement('strong'); title.textContent = product.title;
      const price = document.createElement('span'); price.textContent = formatMoney(product.price);
      link.append(title, price); grid.append(link);
    }
    section.hidden = !grid.children.length;
  } catch { section.hidden = true; }
}

function initCarousels(root = document) {
  root.querySelectorAll('[data-carousel]').forEach(carousel => {
    if (carousel.dataset.initialized) return;
    carousel.dataset.initialized = 'true';
    const track = carousel.querySelector('[data-slider-track]');
    if (!track) return;
    carousel.querySelectorAll('[data-slide]').forEach(button => {
      button.addEventListener('click', () => track.scrollBy({ left: Number(button.dataset.slide) * track.clientWidth * 0.8, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' }));
    });
  });
}
document.addEventListener('shopify:section:load', e => initCarousels(e.target));

function initContactDraft() {
  const form = document.getElementById('ContactForm');
  if (!form) return;
  const key = 'asrary-contact-draft';
  const fields = [...form.querySelectorAll('[name^="contact["]')];
  try {
    if (form.querySelector('.form-status--success')) { sessionStorage.removeItem(key); return; }
    const draft = JSON.parse(sessionStorage.getItem(key) || '{}');
    fields.forEach(field => { if (!field.value && draft[field.name]) field.value = draft[field.name]; });
    form.addEventListener('input', () => {
      try { sessionStorage.setItem(key, JSON.stringify(Object.fromEntries(fields.map(field => [field.name, field.value])))); } catch {}
    });
  } catch {}
}

function initCatalogViews(root = document) {
  root.querySelectorAll('[data-catalog]').forEach(catalog => {
    if (catalog.dataset.viewReady) return;
    catalog.dataset.viewReady = 'true';
    const products = catalog.querySelector('[data-catalog-products]');
    if (!products) return;
    const buttons = catalog.querySelectorAll('[data-catalog-view]');
    function select(view) {
      products.dataset.view = view === 'list' ? 'list' : 'grid';
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.catalogView === products.dataset.view)));
      try { localStorage.setItem('asrary-catalog-view', products.dataset.view); } catch {}
    }
    let previous = 'grid';
    try { previous = localStorage.getItem('asrary-catalog-view') || 'grid'; } catch {}
    select(previous);
    buttons.forEach(button => button.addEventListener('click', () => select(button.dataset.catalogView)));
  });
}

function initOrderNotes() {
  document.addEventListener('input', event => {
    if (!event.target.matches('textarea[name="note"]')) return;
    document.querySelectorAll('textarea[name="note"]').forEach(note => {
      if (note !== event.target) note.value = event.target.value;
    });
  });
}

const heroCleanups = new WeakMap();
function initHeroSlides(root = document) {
  root.querySelectorAll('[data-hero-slideshow]').forEach(hero => {
    if (heroCleanups.has(hero)) return;
    const slides = [...hero.querySelectorAll('[data-hero-slide]')];
    if (!slides.length) { hero.hidden = true; return; }
    const controls = hero.querySelector('[data-hero-controls]');
    const pause = hero.querySelector('[data-hero-pause]');
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    let current = 0, timer, hovered = false, focused = false, inView = true;
    let paused = hero.dataset.autoplay !== 'true' || reduced.matches;
    const interval = Math.max(3000, Number(hero.dataset.interval) || 5000);
    function show(index) {
      current = (index + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', current === i);
        slide.inert = current !== i;
        slide.setAttribute('aria-hidden', String(current !== i));
      });
      hero.querySelector('[data-hero-count]').textContent = `${current + 1} / ${slides.length}`;
    }
    function schedule() {
      clearInterval(timer);
      pause.setAttribute('aria-label', paused ? pause.dataset.playLabel : pause.dataset.pauseLabel);
      pause.textContent = paused ? '▶' : 'Ⅱ';
      if (!paused && !hovered && !focused && inView && !document.hidden && slides.length > 1) timer = setInterval(() => show(current + 1), interval);
    }
    hero.classList.add('is-ready');
    controls.hidden = slides.length < 2;
    show(0);
    hero.querySelector('[data-hero-prev]').onclick = () => { show(current - 1); schedule(); };
    hero.querySelector('[data-hero-next]').onclick = () => { show(current + 1); schedule(); };
    pause.onclick = () => { paused = !paused; schedule(); };
    hero.onmouseenter = () => { hovered = true; schedule(); };
    hero.onmouseleave = () => { hovered = false; schedule(); };
    hero.onfocusin = () => { focused = true; schedule(); };
    hero.onfocusout = event => { focused = hero.contains(event.relatedTarget); schedule(); };
    const onReduced = () => { if (reduced.matches) paused = true; schedule(); };
    reduced.addEventListener('change', onReduced);
    document.addEventListener('visibilitychange', schedule);
    const observer = new IntersectionObserver(entries => { inView = entries[0].isIntersecting; schedule(); });
    observer.observe(hero);
    schedule();
    heroCleanups.set(hero, () => { clearInterval(timer); observer.disconnect(); reduced.removeEventListener('change', onReduced); document.removeEventListener('visibilitychange', schedule); });
  });
}
document.addEventListener('shopify:section:load', event => { initHeroSlides(event.target); initCatalogViews(event.target); });
document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-hero-slideshow]').forEach(hero => heroCleanups.get(hero)?.()));
